import React, { Fragment, useState, useRef, useEffect } from 'react'
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid'
import { Combobox, Dialog, DialogPanel, Transition } from '@headlessui/react'
import { LoadingSpinner } from '../LoadingSpinner/LoadingSpinner'

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

interface SearchModalProps<T> {
  onSelect: (selectectValue: T) => void;
  onDismiss: () => void;
  displayValues: T[];
  filterProperties: string[];
  getDisplayNameForValue: (val: T) => string | JSX.Element;
  onSearchOldOrders?: (query: string) => void;
  isSearchingOldOrders?: boolean;
  oldOrdersData?: T[];
}

export default function SearchModal<T>(props: SearchModalProps<T>) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(true)
  const [hasSearchedOldOrders, setHasSearchedOldOrders] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Focus the input field when the component mounts
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [])

  // Determine which data source to use for filtering
  const dataSource = hasSearchedOldOrders && props.oldOrdersData ? props.oldOrdersData : props.displayValues;

  const filteredValues =
    query === ''
      ? []
      : hasSearchedOldOrders ? dataSource : dataSource.filter((value) =>
          props.filterProperties.some((property) =>
            value[property]?.toString()?.toLowerCase()?.includes(query.toLowerCase())
          )
        );

  const handleSearchOldOrders = () => {
    if (props.onSearchOldOrders && query) {
      setHasSearchedOldOrders(true);
      props.onSearchOldOrders(query);
    }
  };

  const showSearchOldOrdersOption = query !== '';
  const showLoadingSpinner = props.isSearchingOldOrders;
  const showNoResultsMessage = query !== '' && filteredValues.length === 0 && hasSearchedOldOrders && !props.isSearchingOldOrders;

  return (
    <Transition.Root show={open} as={Fragment} afterLeave={() => setQuery('')} appear>
      <Dialog as="div" className="relative z-900" onClose={props.onDismiss}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-25 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-900 w-screen overflow-y-auto p-4 sm:p-6 md:p-20">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <DialogPanel className="mx-auto max-w-xl transform rounded-xl bg-white p-2 shadow-2xl ring-1 ring-black ring-opacity-5 transition-all">
              <Combobox onChange={props.onSelect}>
                <Combobox.Input
                  ref={inputRef}
                  className="w-full rounded-md border-0 bg-gray-100 px-4 py-2.5 text-gray-900 focus:ring-0 sm:text-sm"
                  placeholder="Search..."
                  onChange={(event) => setQuery(event.target.value)}
                  value={query}
                  autoFocus
                />

                {filteredValues.length > 0 && (
                  <Combobox.Options static className="max-h-72 scroll-py-2 overflow-y-auto py-2 text-sm text-gray-800">
                    {filteredValues.map((val: T, index: number) => (
                      <Combobox.Option
                        key={`search-modal-${props.getDisplayNameForValue(val)}-${index}`}
                        value={val}
                        className={({ active }) =>
                          classNames('cursor-default select-none px-4 py-2', active && 'bg-blue-600 text-white')
                        }
                      >
                        {props.getDisplayNameForValue(val)}
                      </Combobox.Option>
                    ))}
                  </Combobox.Options>
                )}

                {showSearchOldOrdersOption && (
                  <div className="py-2 text-sm text-gray-800">
                    <button
                      onClick={handleSearchOldOrders}
                      className="w-full cursor-pointer select-none px-4 py-2 hover:bg-blue-600 hover:text-white rounded transition-colors"
                    >
                      Search old orders?
                    </button>
                  </div>
                )}

                {showLoadingSpinner && (
                  <div className="flex justify-center items-center p-4">
                    <LoadingSpinner />
                    <span className="ml-2 text-sm text-gray-500">Searching old orders...</span>
                  </div>
                )}

                {showNoResultsMessage && (
                  <p className="p-4 text-sm text-gray-500">No orders found.</p>
                )}
              </Combobox>
            </DialogPanel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
